import type { ContestSettings } from '@betterpool/api-types/contests-service';
import { Sport } from '@betterpool/api-types/contests-service';
import { GameStatus } from '@betterpool/api-types/games-service';

import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import type { PlayerDetail } from '~/domains/games/interfaces/player-detail.interface';

import { parseFootballPlayerInfoAfterGameStart } from '../parseEntryPick/parseFootball';
import { parseBasketballPlayerInfoAfterGameStart } from '../parseEntryPick/parseBasketball';
import { parseGolfPlayerInfoAfterGameStart } from '../parseEntryPick/parseGolf';
import type { PlayerInfo, PlayerInfoBase } from '../../types/player';

const parsePlayerInfoAfterGameStart = (playerData: PlayerDetail): Partial<PlayerInfo> => ({
  scoreBreakdown: playerData.scoreBreakdown,
});

const parseFootballData = (playerData: PlayerDetail): Partial<PlayerInfo> => ({
  tag: {
    team: playerData.attributes.team.name,
    position: playerData.attributes.position,
    rank: `#${playerData.attributes.jersey}`,
    injury: playerData.attributes.injury?.abbrStatus,
  },
  isUpcoming:
    !playerData.attributes.gameStatus || playerData.attributes.gameStatus === GameStatus.SCHEDULED,
  ...(playerData.attributes.gameStatus &&
    playerData.attributes.gameStatus !== GameStatus.SCHEDULED && {
      ...parseFootballPlayerInfoAfterGameStart(playerData),
      ...parsePlayerInfoAfterGameStart(playerData),
    }),
});

const parseBasketballData = (playerData: PlayerDetail): Partial<PlayerInfo> => ({
  tag: {
    team: playerData.attributes.team.name,
    position: playerData.attributes.position,
    rank: `#${playerData.attributes.jersey_number}`,
    injury: playerData.attributes.injury?.abbrStatus,
  },
  isUpcoming:
    !playerData.attributes.gameStatus || playerData.attributes.gameStatus === GameStatus.SCHEDULED,
  ...(playerData.attributes.gameStatus &&
    playerData.attributes.gameStatus !== GameStatus.SCHEDULED && {
      ...parseBasketballPlayerInfoAfterGameStart(playerData),
      ...parsePlayerInfoAfterGameStart(playerData),
    }),
});

const parseGolfData = (
  playerData: PlayerDetail,
  scoreType?: ContestSettings['scoreType']
): Partial<PlayerInfo> => ({
  tag: {
    country: playerData.attributes.country,
  },
  isUpcoming:
    !playerData.attributes.gameStatus || playerData.attributes.gameStatus === GameStatus.SCHEDULED,
  ...(playerData.attributes.gameStatus && {
    ...parseGolfPlayerInfoAfterGameStart(playerData, scoreType),
    ...parsePlayerInfoAfterGameStart(playerData),
  }),
});

const parseBaseballData = (playerData: PlayerDetail): Partial<PlayerInfo> => ({
  game: {
    home: {
      id: playerData.attributes.team.id,
      alias: playerData.attributes.team.name,
    },
    away: {
      id: playerData.attributes.team.id,
      alias: playerData.attributes.team.name,
    },
  },
});

const parsers = {
  [Sport.FOOTBALL]: parseFootballData,
  [Sport.GOLF]: parseGolfData,
  [Sport.BASKETBALL]: parseBasketballData,
  [Sport.BASEBALL]: parseBaseballData,
};

const parseBaseData = (playerData: PlayerDetail): PlayerInfoBase => ({
  playerId: playerData.id,
  playerName: playerData.name,
  playerImage: getTeamOrPlayerImageUrl(playerData.leagueAlias, playerData.id),
});

const parsePlayerInfo = (
  playerData: PlayerDetail,
  sport: Sport,
  scoreType?: ContestSettings['scoreType']
): PlayerInfo => {
  const baseData = parseBaseData(playerData);
  const parser = parsers[sport] ?? (() => ({}));

  return {
    ...baseData,
    ...parser(playerData, scoreType),
  };
};

export default parsePlayerInfo;
